import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { StocktakeService } from 'src/core/services/stocktake.service';
import { UtilsService } from 'src/core/services/utils.service';
import { Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';

interface StartAndEndDatePayload {
  start_date: string;
  end_date: string;
}
@Component({
    selector: 'app-stocktake-reports',
    templateUrl: './stocktake-reports.component.html',
    styleUrls: ['./stocktake-reports.component.scss'],
    standalone: false
})
export class StocktakeReportsComponent implements OnInit {
  constructor(
    // private _auth: AuthService,
    private _formBuilder: UntypedFormBuilder,
    // private _route: ActivatedRoute,
    // private _dashboardService: DashboardService,
    // private _prescriptionService: PrescriptionService,
    // private _batchService: BatchService,
    // private _medicineService: MedicineService,
    // private _loaderService: LoaderService,
    // private _individualService: IndividualService,
    // private _companyService: CompanyService,
    // private _router: Router,
    private _utilService: UtilsService,
    private _stocktakeService: StocktakeService
  ) {}
  diffDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
  });

  downloadDiffReportControl = this._formBuilder.control(
    '',
    Validators.required
  );

  scannedDateRangeForm = this._formBuilder.group({
    start: ['', Validators.required],
    end: ['', Validators.required],
    criteria: ['monthly'],
  });
  downloadScannedReportControl = this._formBuilder.control(
    '',
    Validators.required
  );
  seperator: string = ';';
  ngOnInit(): void {}

  // Difference report
  generateAndDownloadDiff() {
    if (this.diffDateRangeForm.invalid) return;

    const payload: StartAndEndDatePayload = {
      start_date: this._utilService.bestBeforeTime(
        this.diffDateRangeForm.value.start.toISOString()
      ),
      end_date: this._utilService.bestBeforeTime(
        this.diffDateRangeForm.value.end.toISOString()
      ),
    };

    if (this.downloadDiffReportControl.value === 'csv')
      this.downloadDiffCSV(payload);

    if (this.downloadDiffReportControl.value === 'excel')
      this.downloadDiffsExcel(payload);
  }

  async downloadDiffCSV(payload: StartAndEndDatePayload) {
    const data = await this._stocktakeService.diffReportStocktake(payload);

    const rows: string[] = [
      `Date${this.seperator}Medicine${this.seperator}Dosage type${this.seperator}Package type${this.seperator}Scan Count${this.seperator}Instock${this.seperator}Additional information`,
    ];

    if (data.length === 0) return;

    for (const item of data) {
      // create individual row
      const attributes: string[] = [
        item.stocktake_date,
        item.medicine,
        item.dosage_type,
        item.medicine_dosage_package_type,
        item.stock_count,
        item.instock_count,
        item.additional_information?.replace(/(\r\n|\n|\r)/gm, ' ') || '',
      ];

      const row: string = attributes.join(this.seperator);
      rows.push(row);
    }

    const csvArray: string = rows.join('\r\n');
    const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'Stocktake_difference.csv');
  }

  async downloadDiffsExcel(payload: StartAndEndDatePayload) {
    const data = await this._stocktakeService.diffReportStocktake(payload);
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Stocktake');

    sheet.columns = [
      { header: 'Date', key: 'stocktake_date', width: 32 },
      { header: 'Medicine', key: 'medicine', width: 32 },
      { header: 'Dosage type', key: 'dosage_type', width: 32 },
      {
        header: 'Package type',
        key: 'medicine_dosage_package_type',
        width: 32,
      },
      { header: 'Scan Count', key: 'stock_count', width: 32 },
      { header: 'Instock', key: 'instock_count', width: 32 },
      {
        header: 'Additional information',
        key: 'additional_information',
        width: 32,
      },
    ];

    if (data.length === 0) return;

    for (const item of data) {
      sheet.addRow(item);
    }

    const buffer = await workBook.xlsx.writeBuffer();

    const blob: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Stocktake_difference.xlsx');
  }

  // scanned report
  generateAndDownloadScanned() {
    if (this.scannedDateRangeForm.invalid) return;

    const payload: StartAndEndDatePayload = {
      start_date: this._utilService.bestBeforeTime(
        this.scannedDateRangeForm.value.start.toISOString()
      ),
      end_date: this._utilService.bestBeforeTime(
        this.scannedDateRangeForm.value.end.toISOString()
      ),
    };

    if (this.downloadScannedReportControl.value === 'csv')
      this.downloadScannedCSV(payload);

    if (this.downloadScannedReportControl.value === 'excel')
      this.downloadScannedsExcel(payload);
  }

  async downloadScannedCSV(payload: StartAndEndDatePayload) {
    const data = await this._stocktakeService.scannedReportStocktake(payload);

    const rows: string[] = [
      `Date${this.seperator}Medicine${this.seperator}Barcode`,
    ];

    if (data.length === 0) return;

    for (const item of data) {
      // create individual row
      const attributes: string[] = [
        item.stocktake_date,
        item.medicine_dosage_package_type,
        item.barcode,
      ];

      const row: string = attributes.join(this.seperator);
      rows.push(row);
    }

    const csvArray: string = rows.join('\r\n');
    const blob: Blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'Stocktake_scanned.csv');
  }

  async downloadScannedsExcel(payload: StartAndEndDatePayload) {
    const data = await this._stocktakeService.scannedReportStocktake(payload);
    const workBook: Workbook = new Workbook();
    workBook.creator = 'John Doe';
    workBook.lastModifiedBy = 'Soul Jah Love';
    workBook.created = new Date();
    // Force workbook calculation on load
    workBook.calcProperties.fullCalcOnLoad = true;

    workBook.views = [
      {
        x: 0,
        y: 0,
        width: 10000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    // Add orders sheet
    const sheet: Worksheet = workBook.addWorksheet('Stocktake');

    sheet.columns = [
      { header: 'Date', key: 'stocktake_date', width: 32 },
      { header: 'Medicine', key: 'medicine_dosage_package_type', width: 32 },
      { header: 'Barcode', key: 'barcode', width: 32 },
    ];

    if (data.length === 0) return;

    for (const item of data) {
      sheet.addRow(item);
    }

    const buffer = await workBook.xlsx.writeBuffer();

    const blob: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Stocktake_scanned.xlsx');
  }
}
